package com.bikompanion.data.watch

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import com.bikompanion.data.cache.LocalStore
import com.bikompanion.data.cache.cacheKey
import com.bikompanion.domain.BikeType
import com.bikompanion.domain.StationWatch
import com.bikompanion.domain.StationWatchRepository
import com.bikompanion.domain.di.AppScope

private const val WatchKey = "station_watches_v1"
private val json = Json { ignoreUnknownKeys = true }

@ContributesBinding(AppScope::class)
@Inject
class StationWatchRepositoryImpl(
    private val store: LocalStore,
) : StationWatchRepository {
    override fun watches(): List<StationWatch> =
        runCatching {
            json.decodeFromString<List<StoredWatch>>(store.read(cacheKey(WatchKey)).orEmpty()).map { it.toDomain() }
        }.getOrDefault(emptyList())

    override fun replace(watch: StationWatch) {
        write(watches().filterNot { it.sameTargetAs(watch) } + watch)
    }

    override fun remove(
        networkId: String,
        stationId: String,
        bikeType: BikeType,
    ) {
        write(watches().filterNot { it.networkId == networkId && it.stationId == stationId && it.bikeType == bikeType })
    }

    override fun removeExpired(nowEpochMillis: Long): List<StationWatch> {
        val current = watches()
        val expired = current.filter { it.expiresAtEpochMillis <= nowEpochMillis }
        if (expired.isNotEmpty()) write(current - expired.toSet())
        return expired
    }

    override fun clear() {
        store.remove(cacheKey(WatchKey))
    }

    private fun write(watches: List<StationWatch>) {
        store.write(cacheKey(WatchKey), json.encodeToString(watches.map { it.toStored() }))
    }
}

@Serializable
private data class StoredWatch(
    val networkId: String,
    val stationId: String,
    val stationName: String,
    val bikeType: String,
    val expiresAtEpochMillis: Long,
    val station: StoredStation? = null,
)

@Serializable
private data class StoredStation(
    val id: String,
    val name: String,
    val networkId: String,
    val latitude: Double,
    val longitude: Double,
    val freeBikes: Int,
    val emptySlots: Int,
    val electricBikes: Int,
    val supportsElectricBikes: Boolean,
)

private fun StationWatch.sameTargetAs(other: StationWatch) =
    networkId == other.networkId && stationId == other.stationId && bikeType == other.bikeType

private fun StationWatch.toStored() =
    StoredWatch(
        networkId = networkId,
        stationId = stationId,
        stationName = stationName,
        bikeType = bikeType.name,
        expiresAtEpochMillis = expiresAtEpochMillis,
        station = station?.toStored(),
    )

private fun StoredWatch.toDomain() =
    StationWatch(
        networkId = networkId,
        stationId = stationId,
        stationName = stationName,
        bikeType = BikeType.entries.firstOrNull { it.name == bikeType } ?: BikeType.Normal,
        expiresAtEpochMillis = expiresAtEpochMillis,
        station = station?.toDomain(),
    )

private fun com.bikompanion.domain.BikeStation.toStored() =
    StoredStation(
        id = id,
        name = name,
        networkId = networkId,
        latitude = latitude,
        longitude = longitude,
        freeBikes = freeBikes,
        emptySlots = emptySlots,
        electricBikes = electricBikes,
        supportsElectricBikes = supportsElectricBikes,
    )

private fun StoredStation.toDomain() =
    com.bikompanion.domain.BikeStation(
        id = id,
        name = name,
        networkId = networkId,
        latitude = latitude,
        longitude = longitude,
        freeBikes = freeBikes,
        emptySlots = emptySlots,
        electricBikes = electricBikes,
        supportsElectricBikes = supportsElectricBikes,
    )
